import { useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";

// import { api_without_cancellation } from "../../api/api";
import { getCaseId, getClientId } from "../../Utils/helper";
import { api_without_cancellation } from "../../api/api";

const MAX_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const UploadRecordingModal = ({ show, onClose, onSuccess }) => {
  const [recordingName, setRecordingName] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const isVideo = file?.type?.startsWith("video/");

  const reset = () => {
    setRecordingName("");
    setFile(null);
    setDragOver(false);
    setUploading(false);
    setProgress(0);
    setError(null);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const validateFile = (f) => {
    if (!f) return "No file selected.";

    const isAV = f.type.startsWith("audio/") || f.type.startsWith("video/");

    if (!isAV) {
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      const AV_EXTENSIONS = new Set([
        // audio
        "mp3",
        "wav",
        "aiff",
        "aif",
        "flac",
        "alac",
        "aac",
        "m4a",
        "wma",
        "ogg",
        "oga",
        // video
        "webm",
        "mkv",
        "flv",
        "vob",
        "ogv",
        "drc",
        "gifv",
        "mng",
        "mpg",
        "mpeg",
        "avi",
        "mov",
        "mts",
        "m2ts",
        "ts",
        "wmv",
        "yuv",
        "rm",
        "qt",
        "mp4",
      ]);
      if (!AV_EXTENSIONS.has(ext)) {
        return "Only audio and video files are allowed.";
      }
    }

    if (f.size > MAX_SIZE_BYTES) return "File exceeds the 2 GB limit.";
    return null;
  };

  const applyFile = (f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
    // Auto-fill name from filename if empty
    if (!recordingName.trim()) {
      const nameWithoutExt = f.name.replace(/\.[^.]+$/, "");
      setRecordingName(nameWithoutExt);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) applyFile(f);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }
    if (!recordingName.trim()) {
      setError("Please enter a recording name.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", recordingName.trim());

      await api_without_cancellation.post(
        // eslint-disable-next-line quotes
        `/api/recordings/${getClientId()}/${getCaseId()}/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      reset();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Upload failed. Please try again."
      );
      setUploading(false);
    }
  };

  const canSubmit = file && recordingName.trim() && !uploading;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="md"
      dialogClassName="modal-dialog modal-dialog-centered"
      backdrop={uploading ? "static" : true}
      keyboard={!uploading}
      contentClassName="custom-modal-new-provider"
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="modal-header text-center p-0 bg-primary popup-heading-color justify-content-center"
      >
        <h5 className="modal-title d-flex align-items-center justify-content-center font-size-20 height-25 font-weight-semibold text-uppercase popup-heading-color font-weight-500">
          Upload Recording
        </h5>
      </Modal.Header>

      <Modal.Body style={{ padding: "5px", minHeight: "max-content" }}>
        {/* Recording Name */}
        <div className="d-flex flex-column" style={{ gap: "5px", marginBottom: "5px" }}>
          <label
            className="mb-0"
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "var(--primary-25)",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Recording Name
          </label>
          <input
            type="text"
            className="form-control p-l-5 p-0 rounded-0 height-25"
            placeholder="Enter a name for this recording…"
            value={recordingName}
            onChange={(e) => setRecordingName(e.target.value)}
            disabled={uploading}
            style={{ fontSize: "14px", fontWeight: "600" }}
            maxLength={200}
          />
        </div>

        {/* Drop Zone */}
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={!uploading ? handleDrop : undefined}
          style={{
            border: `2px dashed ${dragOver ? "var(--primary-25, #1a4f8a)" : file ? "#276c3d" : "#c8d4e8"}`,
            borderRadius: "6px",
            padding: "20px 16px",
            textAlign: "center",
            cursor: uploading ? "not-allowed" : "pointer",
            background: dragOver ? "#e8f0fb" : file ? "#eaf6ee" : "#f8fafc",
            transition: "all 0.15s ease",
            minHeight: "130px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,video/*,.mp3,.wav,.aiff,.aif,.flac,.alac,.aac,.m4a,.wma,.ogg,.oga,.webm,.mkv,.flv,.vob,.ogv,.drc,.gifv,.mng,.mpg,.mpeg,.avi,.mov,.mts,.m2ts,.ts,.wmv,.yuv,.rm,.qt,.mp4"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={uploading}
          />

          {file ? (
            <>
              {/* File icon + info */}
              <i
                className={`ic ic-30 ${isVideo ? "ic-video" : "ic-audio"}`}
                style={{ color: "#276c3d" }}
              />
              <div
                style={{
                  fontWeight: "700",
                  fontSize: "13px",
                  color: "#276c3d",
                  wordBreak: "break-all",
                  maxWidth: "100%",
                }}
              >
                {file.name}
              </div>
              <div style={{ fontSize: "11px", color: "#888", fontWeight: "600" }}>
                {formatFileSize(file.size)} &bull; {file.type || "unknown type"}
              </div>
              {!uploading && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "#1a4f8a",
                    fontWeight: "600",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Change file
                </span>
              )}
            </>
          ) : (
            <>
              <i className="ic ic-upload ic-30" style={{ color: "#8ba5c8" }} />
              <div style={{ fontWeight: "700", fontSize: "13px", color: "#444" }}>
                Drag & drop or{" "}
                <span style={{ color: "var(--primary-25, #1a4f8a)", textDecoration: "underline" }}>
                  browse
                </span>
              </div>
              <div style={{ fontSize: "11px", color: "#888", fontWeight: "500" }}>
                Audio: MP3, WAV, OGG, AAC, FLAC, M4A
              </div>
              <div style={{ fontSize: "11px", color: "#888", fontWeight: "500" }}>
                Video: MP4, MOV, AVI, WebM, MKV &bull; Max 2 GB
              </div>
            </>
          )}
        </div>

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginTop: "10px" }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ marginBottom: "3px" }}
            >
              <span
                style={{ fontSize: "11px", fontWeight: "700", color: "var(--primary-25, #1a4f8a)" }}
              >
                Uploading…
              </span>
              <span
                style={{ fontSize: "11px", fontWeight: "700", color: "var(--primary-25, #1a4f8a)" }}
              >
                {progress}%
              </span>
            </div>
            <div
              style={{
                height: "5px",
                background: "#d0dce8",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  background: "var(--primary-25, #1a4f8a)",
                  borderRadius: "3px",
                  transition: "width 0.2s ease",
                }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && !uploading && (
          <div
            style={{
              marginTop: "8px",
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#c0392b",
            }}
          >
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider d-flex align-items-center justify-content-center"
        style={{ borderTop: "none" }}
      >
        <Button
          variant="secondary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleClose}
          disabled={uploading}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleUpload}
          disabled={!canSubmit}
          style={{ fontSize: 14, fontWeight: 600, gap: "5px" }}
        >
          {uploading ? (
            <>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  border: "2px solid rgba(255,255,255,0.4)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "urm-spin 0.75s linear infinite",
                  flexShrink: 0,
                }}
              />
              Uploading…
            </>
          ) : (
            "Upload"
          )}
        </Button>
      </Modal.Footer>

      <style>{`
        @keyframes urm-spin { to { transform: rotate(360deg); } }
      `}</style>
    </Modal>
  );
};

export default UploadRecordingModal;
